import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { useConfirmDialog } from '~/components/DialogsProvider';
import { fetchEmployee } from '~/data/employee';
import { useEmployee } from '~/data/employee/useEmployee';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { ContactTabSectionHeadingProps } from '~/scenes/PeopleModule/components/Tabs/ContactTab/components/ContactTabSectionHeading';
import { AppDispatch } from '~/store';
import { isValidOptionalPhoneNumber } from '~/utils/helper';

import {
  EmpEmeContactType,
  deleteEmpEmeContactData,
  putEmeContactData,
} from './data';

export type IFormInput = {
  fEmpEmeEmail: string;
  fEmpEmeName: string;
  fEmpEmePhone: string;
};

export type UseEmegencyContractOutput = {
  form: UseFormReturn<IFormInput>;
  actions: Pick<
    ContactTabSectionHeadingProps,
    'onEdit' | 'onSave' | 'onCancel' | 'onDelete'
  >;
  readMode: boolean;
  empData: EmpEmeContactType;
  isLoading: boolean;
};

export const useEmergencyContact = (): UseEmegencyContractOutput => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch: AppDispatch = useDispatch();
  const confirm = useConfirmDialog();

  const [readMode, setReadMode] = useState(true);

  const {
    employee: { empEmeContact: empData, empBasicData },
    isLoading,
  } = useEmployee();

  const emptyFormData: IFormInput = {
    fEmpEmeName: '',
    fEmpEmePhone: '',
    fEmpEmeEmail: '',
  };

  const validationSchema = React.useMemo(() => {
    return yup.object().shape<IFormInput>(
      {
        fEmpEmeName: yup.string().required(t('laEmeContactError2')),
        fEmpEmeEmail: yup
          .string()
          .defined()
          .email(t('laValidEmail'))
          .when('fEmpEmePhone', {
            is: (fEmpEmePhone: string) => !fEmpEmePhone,
            then: yup.string().required(t('laEmeContactError1')),
          }),
        fEmpEmePhone: yup
          .string()
          .defined()
          .when('fEmpEmeEmail', {
            is: (fEmpEmeEmail: string) => !fEmpEmeEmail,
            then: yup.string().required(t('laEmeContactError1')),
          })
          .test(
            'phoneNumberValidation',
            t('phoneInvalid'),
            isValidOptionalPhoneNumber,
          ),
      },
      [
        ['fEmpEmeEmail', 'fEmpEmePhone'],
        ['fEmpEmePhone', 'fEmpEmeEmail'],
      ],
    );
  }, [t]);
  const resolver = useYupValidationResolver(validationSchema);

  const form = useForm<IFormInput>({
    resolver,
    defaultValues: { ...emptyFormData, ...empData },
  });
  const { handleSubmit, reset } = form;

  const handleFormSubmit = React.useMemo(() => {
    return handleSubmit((data) => {
      const json: IFormInput = {
        fEmpEmeEmail: data.fEmpEmeEmail,
        fEmpEmeName: data.fEmpEmeName,
        fEmpEmePhone: data.fEmpEmePhone,
      };
      (async () => {
        try {
          await putEmeContactData(json, empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
          setReadMode(true);
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      })();
    });
  }, [dispatch, empBasicData.fEmpId, enqueueSnackbar, handleSubmit, t]);

  const onSubmit = () => {
    handleFormSubmit();
  };

  const handleFormReset = (totalReset: boolean) => {
    if (totalReset) {
      reset(emptyFormData);
    } else {
      reset();
    }
    setReadMode(true);
  };

  const deleteContact = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    confirm(t('contactDeleteConfirmation'), async (confirmed) => {
      if (confirmed) {
        try {
          await deleteEmpEmeContactData(empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          handleFormReset(true);
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      }
    });
  };

  return {
    form,
    actions: {
      onEdit: () => setReadMode(false),
      onSave: onSubmit,
      onCancel: () => handleFormReset(false),
      onDelete: deleteContact,
    },
    readMode,
    empData,
    isLoading,
  };
};
